// stores/user.ts - 用户认证状态管理 Pinia Store
// Phase3：
// - 使用 useAuthApi 实现 login/register/logout 真实接口
// - 统一 Token 持久化与刷新（如有 refreshToken），401 失效清理并唤起登录
// - 登录/登出后触发关联模块刷新（配额、任务等）

import { defineStore } from 'pinia'
import type { LoginData, UserInfo } from '~/types/user'
import { useTranslationStore } from '~/stores/translation'
import { useAuthApi } from '~/composables/useAuthApi'
import { useUserMixApi } from '~/composables/useUserMixApi'
import { useMyPageStore } from '~/stores/myPage'
import { FUNNEL_EVENTS, trackFunnelEvent } from '~/utils/gaFunnelEvents.js'

// 简易本地存储工具（与后续composable保持一致接口）
const storage = {
  get<T>(key: string): T | null {
    if (import.meta.server) return null
    try {
      const raw = localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as T) : null
    } catch (e) {
      // 最小化吞错：记录日志并返回空
      // eslint-disable-next-line no-console
      console.warn('[storage.get] JSON parse failed for key=', key, e)
      return null
    }
  },
  set<T>(key: string, value: T) {
    if (import.meta.server) return
    localStorage.setItem(key, JSON.stringify(value))
  },
  remove(key: string) {
    if (import.meta.server) return
    localStorage.removeItem(key)
  }
}

// ==================== Store定义 ====================

export const useUserStore = defineStore('user', {
  state: () => ({
    // 是否登录
    isLoggedIn: false,

    // 登录信息
    loginData: null as LoginData | null,

    // 用户信息
    info: null as UserInfo | null,

    // 用户资料加载态（供 Sidebar 骨架/错误 UI 使用）
    userLoading: false,
    userLoaded: false,
    userError: false,

    // UI 辅助状态
    dropdownOpen: false,
    authModalVisible: false
  }),

  getters: {
    // 默认头像（与移动端统一）
    // 来自 doc_translate_master/assets/icons/avatar_default.png，已复制到网站静态资源
    // 注意：放在 getters 内部以便 SSR/客户端均可使用静态路径
    defaultAvatar(): string {
      return '/images/my-page/avatar_default.png'
    },
    // 用户名称
    userName: (state): string => {
      return state.info?.name || state.loginData?.userName || '未登录'
    },

    // 用户头像
    userAvatar: (state): string => {
      // 优先使用 profile/avatarUrl 或 loginData.avatarUrl，否则回退到本地静态图
      return state.info?.avatarUrl || state.loginData?.avatarUrl || '/images/my-page/avatar_default.png'
    }
  },

  actions: {
    /**
     * 初始化：从本地存储恢复登录态
     */
    initFromStorage() {
      const data = storage.get<LoginData>('auth.loginData')
      const info = storage.get<UserInfo>('auth.userInfo')
      if (data) {
        this.loginData = data
        this.isLoggedIn = true
      }
      if (info) {
        this.info = info
      }
    },
    // ==================== 登录相关 ====================

    /**
     * 将登录信息写入 Store 与本地存储，并串联后续流程
     */
    async setLoginData(loginData: LoginData, options?: { closeAuthModal?: boolean; fetchProfile?: boolean }) {
      const normalized: LoginData = {
        ...loginData,
        userName: loginData.userName || `user_${loginData.uid}`,
        avatarUrl: loginData.avatarUrl || '/images/my-page/avatar_default.png'
      }

      this.isLoggedIn = true
      this.loginData = normalized
      storage.set('auth.loginData', normalized)

      if (options?.fetchProfile ?? true) {
        try {
          await this.fetchUserInfoFromUserMix()
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[user.setLoginData] fetchUserInfoFromUserMix failed', e)
        }
      }

      // 登录成功漏斗事件（埋点异常不得影响主流程）
      trackFunnelEvent(FUNNEL_EVENTS.AUTH_SUCCESS)

      if (options?.closeAuthModal ?? true) {
        this.setAuthModalVisible(false)
      }
    },

    /**
     * 用户登录（Phase3 实装：设备登录）
     * 说明：MVP 使用设备登录替代用户名/密码登录，username 仅用于展示头像/昵称
     */
    async login(username: string, _password?: string) {
      await this.loginWithDevice(username)
    },

    /**
     * 用户注册
     * 第2阶段：模拟注册成功
     * 第3阶段：替换为真实API调用
     */
    async register(username: string, _password?: string) {
      // MVP: 注册流程与登录相同（设备登录），以保留交互形态
      await this.loginWithDevice(username)
    },

    /**
     * 设备登录（真实 API）
     * - 生成/读取稳定的 deviceId
     * - 调用后端 /account/login/device
     * - 持久化 loginData 和 userInfo
     */
    async loginWithDevice(nickname?: string) {
      const { loginByDevice } = useAuthApi()

      // 生成/获取稳定 deviceId
      let deviceId = storage.get<string>('auth.deviceId') || ''
      if (!deviceId) {
        const uuid = cryptoRandomUuid()
        deviceId = `web_${uuid}`
        storage.set('auth.deviceId', deviceId)
      }

      const data = await loginByDevice(deviceId)
      // 将昵称用于展示（后端暂不返回）
      const finalLogin: LoginData = {
        ...data,
        userName: nickname || data.userName || `user_${data.uid}`,
        // 与移动端统一默认头像，移除外部 Dicebear 依赖
        avatarUrl: data.avatarUrl || '/images/my-page/avatar_default.png'
      }

      await this.setLoginData(finalLogin, { closeAuthModal: false })
    },

    /**
     * 用户登出
     */
    async logout() {
      // 调用后端登出，失败忽略
      try {
        const { logout } = useAuthApi()
        await logout()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[user.logout] backend logout failed', e)
      }

      // 清空登录状态
      this.isLoggedIn = false
      this.loginData = null
      this.info = null
      this.userLoading = false
      this.userLoaded = false
      this.userError = false
      this.dropdownOpen = false
      this.authModalVisible = false

      // 清理本地持久化
      storage.remove('auth.loginData')
      storage.remove('auth.userInfo')

      // 串联：停止所有翻译轮询并清空任务，避免登出后仍在拉取
      const translationStore = useTranslationStore()
      try { translationStore.stopAllPolling() } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[user.logout] stopAllPolling failed', e)
      }
      translationStore.clearAllTasks()
    },

    /**
     * 清理登录态（供拦截器在登录过期时调用）
     * 不触发额外业务操作，仅移除本地数据与状态
     */
    clearLoginState() {
      this.isLoggedIn = false
      this.loginData = null
      this.info = null
      this.dropdownOpen = false
      this.authModalVisible = false
      storage.remove('auth.loginData')
      storage.remove('auth.userInfo')
    },

    /**
     * 刷新后更新 Token（供拦截器调用）
     */
    updateTokens(token: string, refreshToken?: string) {
      if (!this.loginData) return
      this.loginData = { ...this.loginData, token, refreshToken: refreshToken || this.loginData.refreshToken }
      storage.set('auth.loginData', this.loginData)
    },

    /**
     * 获取用户详细信息
     * 第2阶段：使用loginData生成模拟UserInfo
     * 第3阶段：替换为真实API调用
     */
    async fetchUserInfo() {
      if (!this.loginData) {
        return
      }

      // 模拟延迟
      await new Promise(resolve => setTimeout(resolve, 300))

      // 生成模拟用户信息
      const info: UserInfo = {
        id: this.loginData.uid,
        name: this.loginData.userName,
        avatarUrl: this.loginData.avatarUrl
      }
      this.info = info
      storage.set('auth.userInfo', info)
    },

    /**
     * 从 usermix 聚合接口获取用户信息，并联动同步会员与配额
     * - 若失败：提示由全局拦截器完成，此处仅设置错误态与降级
     */
    async fetchUserInfoFromUserMix() {
      if (!this.isLoggedIn || !this.loginData) {
        return
      }

      const { getUserMixInfo } = useUserMixApi()
      const myPageStore = useMyPageStore()
      this.userLoading = true
      this.userLoaded = false
      this.userError = false
      try {
        const { profile, vipList, docTranslateInfo } = await getUserMixInfo()
        const info: UserInfo = { id: profile.id, name: profile.name || this.loginData.userName, avatarUrl: profile.avatarUrl || this.loginData.avatarUrl }
        this.info = info
        storage.set('auth.userInfo', info)

        // 同步会员与配额（以 usermix 为唯一来源）
        try {
          myPageStore.syncMembershipFromMix(docTranslateInfo, vipList)
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[user.fetchUserInfoFromUserMix] syncMembershipFromMix failed', e)
        }

        this.userError = false
      } catch {
        this.userError = true
      } finally {
        this.userLoading = false
        this.userLoaded = true
      }
    },

    setAuthModalVisible(visible: boolean) {
      this.authModalVisible = visible
      if (visible) {
        trackFunnelEvent(FUNNEL_EVENTS.AUTH_OPEN)
      }
    },

    setDropdownOpen(open: boolean) {
      this.dropdownOpen = open
    },

    // 微信扫码登录流程由 useWeChatAuth + 回调页直接复用 setLoginData
  }
})

// ============ 本文件内工具函数 ============
function cryptoRandomUuid(): string {
  // 尝试使用 Web Crypto API 生成 UUID
  try {
    // 现代浏览器支持
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug('[cryptoRandomUuid] crypto.randomUUID not available', e)
  }

  // 回退方案：手动生成 UUID v4
  const s = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
  return s
}

// 检测是否为旧的 Dicebear 头像链接
//（已弃用）不再对历史 Dicebear 链接做兼容迁移
